interface ErrorLogPayload {
  // Required fields
  endpoint: string;
  method: string;
  pageUrl?: string;
  sessionId?: string;

  // User context
  userId?: number;
  userRole?: string;

  // API Request details
  statusCode?: number;
  requestBody?: any;
  requestHeaders?: any;
  responseBody?: any;
  responseHeaders?: any;

  // Error details
  errorType?: 'server' | 'network' | 'client';
  errorName?: string;
  errorMessage?: string;
  errorStack?: string;
  errorCode?: string;

  // Browser/Client context
  userAgent?: string;
  clientInfo?: {
    browser?: string;
    browserVersion?: string;
    os?: string;
    osVersion?: string;
    deviceType?: string;
  };

  // Network context
  geoLocation?: {
    country?: string;
    city?: string;
  };
  networkInfo?: {
    connectionType?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    isOnline?: boolean;
  };

  // Timing details
  requestDuration?: number;
  timingDetails?: {
    ttfb?: number;
    dnsLookup?: number;
    tcpConnection?: number;
    tlsNegotiation?: number;
  };
  timestampClient?: string;

  // Page/Route context
  pageTitle?: string;
  currentRoute?: string;
  routeParams?: any;
  queryParams?: any;
  routerHistory?: Array<{ route: string; timestamp: string }>;

  // React State context
  componentName?: string;
  componentStack?: string;
  reactStates?: any;
  reduxState?: any;
  contextValues?: any;
  localStorage?: any;
  sessionStorage?: any;

  // Memory & Performance
  memoryUsedMb?: number;
  memoryLimitMb?: number;
  performanceMetrics?: {
    lcp?: number;
    fid?: number;
    cls?: number;
    fcp?: number;
    cpuCores?: number;
  };

  // User actions
  userActions?: Array<{
    type: string;
    target?: string;
    position?: number;
    field?: string;
    timestamp: string;
  }>;

  // Form context
  formId?: string;
  formData?: any;
  formValidationErrors?: any;

  // API retry context
  retryCount?: number;
  retryAttempts?: Array<{ timestamp: string; statusCode: number }>;

  // Session context
  sessionDuration?: number;
  sessionPageViews?: number;
  sessionErrorsCount?: number;

  // Feature flags
  featureFlags?: any;
  abTests?: any;

  // Additional metadata
  buildVersion?: string;
  environment?: string;
  isFirstError?: boolean;
  isCritical?: boolean;
  tags?: string[];
  customData?: any;
}

let sessionId: string | null = null;
let userActionsBreadcrumbs: Array<any> = [];

// Generate or retrieve session ID
function getSessionId(): string {
  if (typeof window === 'undefined') return 'server-side';
  
  if (!sessionId) {
    sessionId = sessionStorage.getItem('error-log-session-id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('error-log-session-id', sessionId);
    }
  }
  return sessionId;
}

// Collect browser/client info
function getClientInfo() {
  if (typeof window === 'undefined') return undefined;
  
  const ua = navigator.userAgent;
  return {
    browser: getBrowserName(ua),
    browserVersion: getBrowserVersion(ua),
    os: getOSName(ua),
    deviceType: getDeviceType(ua),
  };
}

function getBrowserName(ua: string): string {
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getBrowserVersion(ua: string): string {
  const match = ua.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/);
  return match ? match[2] : 'Unknown';
}

function getOSName(ua: string): string {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS')) return 'iOS';
  return 'Unknown';
}

function getDeviceType(ua: string): string {
  if (ua.includes('Mobile')) return 'mobile';
  if (ua.includes('Tablet')) return 'tablet';
  return 'desktop';
}

// Get network info
function getNetworkInfo() {
  if (typeof window === 'undefined' || !('connection' in navigator)) return undefined;
  
  const conn = (navigator as any).connection;
  return {
    connectionType: conn?.type,
    effectiveType: conn?.effectiveType,
    downlink: conn?.downlink,
    rtt: conn?.rtt,
    isOnline: navigator.onLine,
  };
}

// Get memory info
function getMemoryInfo() {
  if (typeof window === 'undefined' || !(performance as any).memory) return undefined;
  
  const memory = (performance as any).memory;
  return {
    memoryUsedMb: memory.usedJSHeapSize / (1024 * 1024),
    memoryLimitMb: memory.jsHeapSizeLimit / (1024 * 1024),
  };
}

// Get performance metrics
function getPerformanceMetrics() {
  if (typeof window === 'undefined') return undefined;
  
  return {
    cpuCores: navigator.hardwareConcurrency,
  };
}

// Track user actions
export function trackUserAction(action: { type: string; target?: string; position?: number; field?: string }) {
  if (typeof window === 'undefined') return;
  
  userActionsBreadcrumbs.push({
    ...action,
    timestamp: new Date().toISOString(),
  });
  
  // Keep only last 20 actions
  if (userActionsBreadcrumbs.length > 20) {
    userActionsBreadcrumbs = userActionsBreadcrumbs.slice(-20);
  }
}

// Main error logging function
export async function logError(payload: ErrorLogPayload): Promise<void> {
  try {
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
    if (!baseUrl) {
      console.warn('NEXT_PUBLIC_BASE_URL not configured, skipping error logging');
      return;
    }

    const memoryInfo = getMemoryInfo();
    
    const fullPayload: ErrorLogPayload = {
      ...payload,
      pageUrl: payload.pageUrl || (typeof window !== 'undefined' ? window.location.href : 'server-side'),
      sessionId: payload.sessionId || getSessionId(),
      userAgent: typeof window !== 'undefined' ? navigator.userAgent : undefined,
      clientInfo: getClientInfo(),
      networkInfo: getNetworkInfo(),
      memoryUsedMb: memoryInfo?.memoryUsedMb,
      memoryLimitMb: memoryInfo?.memoryLimitMb,
      performanceMetrics: getPerformanceMetrics(),
      pageTitle: typeof window !== 'undefined' ? document.title : undefined,
      currentRoute: typeof window !== 'undefined' ? window.location.pathname : undefined,
      queryParams: typeof window !== 'undefined' ? Object.fromEntries(new URLSearchParams(window.location.search)) : undefined,
      userActions: userActionsBreadcrumbs.length > 0 ? userActionsBreadcrumbs : undefined,
      localStorage: typeof window !== 'undefined' ? { ...localStorage } : undefined,
      sessionStorage: typeof window !== 'undefined' ? { ...sessionStorage } : undefined,
      environment: process.env.NODE_ENV,
      timestampClient: payload.timestampClient || new Date().toISOString(),
    };

    // Send error log (don't await to avoid blocking)
    fetch(`${baseUrl}/errors/log`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fullPayload),
      // Use keepalive to ensure request completes even if page unloads
      keepalive: true,
    }).catch((err) => {
      console.error('Failed to log error:', err);
    });
  } catch (err) {
    console.error('Error in logError function:', err);
  }
}
